import { TranslateService } from "@ngx-translate/core";

import { Observable, Subscription, zip } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  getCategoriesForListByUsage,
  getPropertiesForListByUsage,
  getSitesTableConfig,
  saveSitesTableConfig,
} from "src/app/store/actions/sites/sites.actions";
import {
  AppState,
  loadedCategoriesListSiteState,
  loadedPropertiesListSiteState,
  sitesStateExcelSitesList,
  sitesStateIsLoading,
  sitesStateSitesList,
  sitesStateTotalSites,
} from "src/app/store/reducers";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  ExportEvent,
  FieldType,
  FormatType,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteDTO } from "src/app/models/Sites/siteDTO";

import { getColorByBgColor, getColorFromStr } from "../../utils/colorUtils";
import { setCategories, setProperties } from "../../utils/tableUtils";

const USAGE_CODE = "SITE";

export class BaseSitesList {
  isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  siteList$: Observable<SiteDTO[]>;
  siteList: SiteDTO[];
  excelSiteList$: Observable<SiteDTO[]>;
  totalSites$: Observable<number>;

  siteListSub: Subscription;
  multiSubs: Subscription;

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  formPath: string = "sites/site";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "SiteManagement.Code",
      field: "code",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "SiteManagement.SiteNumber",
      field: "siteNumber",
      fieldType: FieldType.STRING,
      searchable: true,
      style: "font-weight: 600",
    },
    {
      header: "SiteManagement.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.RegionDescription",
      field: "region.description",
      fieldType: FieldType.CHIP_STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.Profile",
      field: "siteProfile.description",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "SiteManagement.Enabled",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header:"SiteManagement.ledSite",
      field: "ledSite",
      fieldType: FieldType.BOOLEAN
    },
    {
      header: "SiteGroup.siteGroup",
      field: "siteGroup.description",
      fieldType: FieldType.STRING,
    },
    {
      header: "SiteManagement.nDispensers",
      field: "siteDevices.nDispensers",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.nFccs",
      field: "siteDevices.nFccs",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.nFuelPoints",
      field: "siteDevices.nFuelPoints",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.nPos",
      field: "siteDevices.nPos",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.nWarehouses",
      field: "siteDevices.nWarehouses",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.Created",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "SiteManagement.comments",
      field: "comment",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  constructor(
    public store: Store<AppState>,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
    this.siteList$ = this.store.pipe(select(sitesStateSitesList));
    this.excelSiteList$ = this.store.pipe(select(sitesStateExcelSitesList));
    this.totalSites$ = this.store.pipe(select(sitesStateTotalSites));
    this.categories$ = this.store.pipe(select(loadedCategoriesListSiteState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListSiteState));
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Sites")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }


  setTableCustomElements() {
    this.siteListSub = this.siteList$
      .pipe(skip(1))
      .subscribe((sites: any[]) => {
        this.siteList = sites;

        if (sites) {
          this.siteList = this.siteList.map((site) => {
            let processedSite = site;
            processedSite = this.setCustomSiteDetails(processedSite);
            return processedSite;
          });
        }
      });
  }

  setCustomSiteDetails(siteDetail: SiteDTO) {
    const customSiteDetail = {
      ...siteDetail,
    };

    var regionDescriptionData = null;
    if (siteDetail?.region?.description) {
      regionDescriptionData = {
        value: siteDetail.region.description,
        icon: "place",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.region?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.region?.code) +
          `;`,
      };
    }

    var siteProfileDescriptionData = null;
    if (siteDetail?.siteProfile?.description) {
      siteProfileDescriptionData = {
        value: siteDetail.siteProfile.description,
        //icon: "local_gas_station",
        style:
          `background-color: ` +
          getColorFromStr(siteDetail.siteProfile?.code) +
          `; color: ` +
          getColorByBgColor(siteDetail.siteProfile?.code) +
          `;`,
      };
    }

    return {
      ...customSiteDetail,
      region: {
        ...customSiteDetail.region,
        description: regionDescriptionData,
      },
      siteProfile: {
        ...customSiteDetail.siteProfile,
        description: siteProfileDescriptionData,
      },
    };
  }

  getSitesCategoriesProperties(usageCode: string, getSites: Function) {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories.length) {
        const tableConfigCategories = setCategories(categories);

        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties.length) {
        const tableConfigProperties = setProperties(properties);

        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig(usageCode);
      getSites();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  getTableConfig(usageCode: string) {
    this.store.dispatch(
      getSitesTableConfig({
        options: {
          code: usageCode,
          settingType: ConfigSettingType.TABLE_CONFIG,
        },
      })
    );
  }

  getTableData(tableEvent: TableEvent, getSites: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    getSites(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  getExportData(exportEvent: ExportEvent, getSitesExcelList: Function) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    getSitesExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  saveTableConfig(tableColumnInfo: ColumnSorterInfo[], usageCode: string) {
    this.store.dispatch(
      saveSitesTableConfig({
        config: {
          code: usageCode,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }
}
